import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import '../styles/Modal.css';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

// `trapFocus` keeps Tab inside the dialog, closes on Escape and restores focus on close (opt-in).
const Modal = ({ isOpen, onClose, children, trapFocus = false, labelledBy, closeLabel = "Close modal" }) => {
  const boxRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen || !trapFocus) return undefined;
    const previous = document.activeElement;
    const box = boxRef.current;
    const focusables = () => Array.from(box?.querySelectorAll(FOCUSABLE) || [])
      .filter((el) => !el.closest('[aria-hidden="true"]'));
    const initial = box?.querySelector('[data-autofocus]') || focusables().find((el) => !el.classList.contains('modal-close-button'));
    (initial || box)?.focus();

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) {
        event.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || !box.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !box.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      if (previous && typeof previous.focus === 'function' && document.contains(previous)) previous.focus();
    };
  }, [isOpen, trapFocus]);

  if (!isOpen) return null;

  const modal = (
    <div className="modal-overlay" onClick={onClose}>
      <div
        ref={boxRef}
        className="modal-box"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={trapFocus ? -1 : undefined}
      >
        <button
          className="modal-close-button"
          onClick={onClose}
          aria-label={closeLabel}
        >
          ×
        </button>

        <div className="modal-content">
          {children}
        </div>
      </div>
    </div>
  );

  // Rendering at the document root keeps the fixed overlay independent of page containers.
  return createPortal(modal, document.body);
};

export default Modal;
